/**
 * Format an AI model API ID into a human-readable display name.
 *
 * Used to display the actual model that produced a saved review/scan, not the
 * currently-configured model. New reviews/scans embed the model id in their
 * response JSON; this formatter renders any known id pattern.
 *
 * Patterns handled:
 *   claude-opus-4-7              → "Claude Opus 4.7"
 *   claude-opus-4-7-20260423     → "Claude Opus 4.7"
 *   claude-sonnet-4-6            → "Claude Sonnet 4.6"
 *   claude-haiku-4-5-20251001    → "Claude Haiku 4.5"
 *   claude-fable-5               → "Claude Fable 5"
 *   gpt-5.5                      → "GPT 5.5"
 *   gpt-5.5-2026-04-23           → "GPT 5.5"
 *   gpt-4o                       → "GPT 4o"
 *
 * Falls back to the raw id if no pattern matches, so unknown future models
 * still display something rather than throwing.
 */
export function formatModelName(modelId) {
  if (!modelId || typeof modelId !== 'string') return null;

  // Claude two-number: claude-{variant}-{major}-{minor}[-...]
  const claudeMatch = modelId.match(/^claude-([a-z]+)-(\d+)-(\d+)/i);
  if (claudeMatch) {
    const [, variant, major, minor] = claudeMatch;
    const cap = variant.charAt(0).toUpperCase() + variant.slice(1).toLowerCase();
    return `Claude ${cap} ${major}.${minor}`;
  }

  // Claude single-number: claude-{variant}-{major}[-date] (e.g. claude-fable-5)
  const claudeSingle = modelId.match(/^claude-([a-z]+)-(\d+)(?:-|$)/i);
  if (claudeSingle) {
    const [, variant, major] = claudeSingle;
    const cap = variant.charAt(0).toUpperCase() + variant.slice(1).toLowerCase();
    return `Claude ${cap} ${major}`;
  }

  // GPT: gpt-{version}[-...]   (version may be like "5.5" or "4o")
  const gptMatch = modelId.match(/^gpt-([\d.]+[a-z]?)/i);
  if (gptMatch) {
    return `GPT ${gptMatch[1]}`;
  }

  return modelId;
}
